"use client";

import { SiteLink } from "@i18n/SiteLink";
import {
	pitchRows,
	prWorkflowMiniRows,
} from "@marketing/shared/lib/bento-fixtures";
import { AipvsTierBadge } from "@saas/publisher-lookup/components/AipvsTierBadge";
import { cn } from "@ui/lib";
import { ArrowDownIcon, ArrowRightIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import type { ReactNode } from "react";
import { CoverageGroupHeader } from "./CoverageReportTiles";
import { CoverageFunnelTile } from "./DataTiles";
import { BlocksAiLabel, PitchStatusChip } from "./MediaListTiles";

/**
 * The /for-pr-firms "Before and after the story runs" block: Media Lists and
 * Coverage Reports (in the app: Pitch Lists and Coverage Groups) as two ends
 * of one campaign, joined by the Placed hand-off. Each card has a small visual
 * so the pair reads at a glance; the full versions live on each product page.
 * Copy reads from `marketing.forPRFirms.v2.workflow`.
 */

const NS = "marketing.forPRFirms.v2.workflow";

/** Three rows of the pitch list: placed, in flight, and the blind spot. */
export function MiniPitchTable({ className }: { className?: string }) {
	const t = useTranslations(NS);
	const rows = prWorkflowMiniRows
		.map((id) => pitchRows.find((r) => r.id === id))
		.filter((r): r is NonNullable<typeof r> => r != null);
	return (
		<ul
			className={cn(
				"@container divide-y rounded-xl border bg-background",
				className,
			)}
		>
			{rows.map((row) => (
				<li
					key={row.id}
					className="flex items-center justify-between gap-3 px-3 py-2"
				>
					<div className="min-w-0">
						<p className="truncate text-xs font-semibold">
							{row.name}
						</p>
						{row.blocksAi ? (
							<BlocksAiLabel className="mt-0.5" />
						) : (
							<p className="truncate text-[11px] text-muted-foreground">
								{t("aiCitations", { count: row.citations })}
							</p>
						)}
					</div>
					<span className="flex shrink-0 items-center gap-2">
						<span className="hidden font-mono text-sm font-semibold tabular-nums @xs:inline">
							{row.aipvs.score}
						</span>
						<AipvsTierBadge tier={row.aipvs.tier} />
						<span className="hidden @sm:inline">
							<PitchStatusChip status={row.status} />
						</span>
					</span>
				</li>
			))}
		</ul>
	);
}

function WorkflowCard({
	stage,
	title,
	body,
	linkLabel,
	href,
	children,
}: {
	stage: string;
	title: string;
	body: string;
	linkLabel: string;
	/** With `href` the label is a real link; without it (mockups) inert. */
	href?: string;
	children: ReactNode;
}) {
	const label = `${linkLabel} →`;
	const labelClass = "mt-5 text-sm font-bold text-primary";
	return (
		<div className="flex h-full min-w-0 flex-col rounded-2xl border bg-card p-5 sm:p-6">
			<p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
				{stage}
			</p>
			<h3 className="mt-1 text-xl font-extrabold tracking-tight">
				{title}
			</h3>
			<p className="mt-2 text-sm text-muted-foreground">{body}</p>
			<div className="mt-5 flex-1">{children}</div>
			{href ? (
				<SiteLink
					href={href}
					className={cn(labelClass, "w-fit hover:underline")}
				>
					{label}
				</SiteLink>
			) : (
				<span className={labelClass}>{label}</span>
			)}
		</div>
	);
}

/** The hand-off between the cards: sideways on desktop, down on phones. */
function PlacedConnector() {
	const t = useTranslations(NS);
	return (
		<div className="flex items-center justify-center lg:flex-col">
			<div className="flex flex-col items-center gap-1.5 py-1 lg:w-36 lg:py-0">
				<ArrowDownIcon
					className="size-4 text-muted-foreground lg:hidden"
					aria-hidden
				/>
				<span className="rounded-full border bg-background px-3 py-1 text-center text-[11px] font-semibold leading-snug text-foreground">
					{t("connector")}
				</span>
				<ArrowRightIcon
					className="hidden size-4 text-muted-foreground lg:block"
					aria-hidden
				/>
			</div>
		</div>
	);
}

export function PrWorkflowBlock({
	hrefs,
	className,
}: {
	/** Card destinations. Omit in mockups for inert labels. */
	hrefs?: { mediaList: string; coverageReport: string };
	className?: string;
}) {
	const t = useTranslations(NS);
	return (
		<div
			className={cn(
				"grid grid-cols-1 items-stretch gap-4 lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]",
				className,
			)}
		>
			<WorkflowCard
				stage={t("before.stage")}
				title={t("before.title")}
				body={t("before.body")}
				linkLabel={t("before.link")}
				href={hrefs?.mediaList}
			>
				<MiniPitchTable />
			</WorkflowCard>
			<PlacedConnector />
			<WorkflowCard
				stage={t("after.stage")}
				title={t("after.title")}
				body={t("after.body")}
				linkLabel={t("after.link")}
				href={hrefs?.coverageReport}
			>
				<div className="rounded-xl border bg-background p-3.5">
					<CoverageGroupHeader className="mb-3 text-[11px]" />
					<CoverageFunnelTile />
				</div>
			</WorkflowCard>
		</div>
	);
}
